<script setup lang="ts">
/**
 * The chat panel: the open conversation (header, thread, composer) or the welcome screen, beside an
 * optional document-preview or law-citation panel. Split in MAR-948 — the parts are `ChatHeader`,
 * `ChatWelcome` and `ChatComposer`, and the behaviour lives in `useChatTurn` (sending, silent tools,
 * confirm cards) and `useChatUploads` (files dropped or picked). What stays here is the wiring, the
 * drop target, and the first load.
 */
import { FileText, Loader2 } from 'lucide-vue-next'
import { createReusableTemplate } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { useDocumentsStore } from '~/stores/documents'
import { useDocumentsIndexingStore } from '~/stores/documentsIndexing'
import ChatHeader from '~/components/chat/ChatHeader.vue'
import ChatWelcome from '~/components/chat/ChatWelcome.vue'
import ChatComposer from '~/components/chat/ChatComposer.vue'
import ChatDocumentPicker from '~/components/chat/ChatDocumentPicker.vue'
import ChatDocPreviewPanel from '~/components/chat/ChatDocPreviewPanel.vue'
import FileDropOverlay from '~/components/FileDropOverlay.vue'
import { ResizeHandle } from '~/components/ui/resize-handle'
import { useScopePanel } from '~/composables/web-chat/useScopePanel'
import { useOpenScope } from '~/composables/web-chat/useOpenScope'
import { createSilentDispatch } from '~/composables/web-chat/dispatch'
import { acceptsChatDrop, documentsFromChatDrop } from '~/composables/web-chat/chatDrop'
import { useWebChatView } from '~/composables/web-chat/useViewState'
import { useChatTurn } from '~/composables/web-chat/useChatTurn'
import { useChatUploads } from '~/composables/web-chat/useChatUploads'
import { useModelSelector } from '~/composables/useModelSelector'
import { useDragHighlight } from '~/composables/useDragHighlight'

const props = withDefaults(defineProps<{ compact?: boolean }>(), { compact: false })

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const webChat = useWebChatStore()
const documents = useDocumentsStore()
const indexing = useDocumentsIndexingStore()
const { close: closeScopePanel } = useScopePanel()
// `open_case_or_client` — resolves the id against the org's own cases, then moves the chat's scope.
const { openScope } = useOpenScope()
const { currentViewState } = useWebChatView()
const { requestHighlight, openDocsDrawer, lawRequest, requestSheetFilter, requestSheetSelect } = usePanelLayout()
const { previewDocument: docPreviewRequest } = storeToRefs(webChat)

/** The header keeps its own band — instead of teleporting into the shared `AppBar` — whenever that
 *  strip isn't there to take it: floating (`compact`), or beside a document preview (`AppBar` hides
 *  itself then). `compact` is exactly `contentActive` in `PanelLayout`, so chat only teleports when no
 *  page exists to claim the bar — which is why this is a plain Teleport, not a `useAppBarSlot` claim. */
const ownHeaderBand = computed(() => props.compact || !!docPreviewRequest.value)
/** The rail toggle normally lives in `AppBar` too; it moves into the header while a preview is open. */
const showRailToggle = computed(() => !!docPreviewRequest.value)
/** The floating window (`compact`) is ~420 px wide whatever the viewport, so the `md:` side-by-side
 *  split the full-page chat uses would leave a sliver of thread beside a 400 px panel (MAR-1484).
 *  There the article or preview COVERS the window instead, as it does on a phone — and the thread
 *  under it is `inert`, so a citation it hides can neither be hovered nor re-focused into reopening
 *  its card over the page. */
const previewCoversThread = computed(() => props.compact && !!(lawRequest.value || docPreviewRequest.value))

const chatInput = ref('')
const documentPickerOpen = ref(false)
const chatMessagesRef = ref<{ requestScroll: () => void; scrollToBottomNow: () => void } | null>(null)
const cameFromHistory = ref(false)

// Silent client tools execute in the UI once the stream completes. Exhaustive over the registry's
// silent ids — see app/composables/web-chat/dispatch.ts.
const silentDispatch = createSilentDispatch({
  router,
  requestHighlight,
  browser: { filterFiles: documents.filterFiles, selectFiles: documents.selectFiles },
  requestSheetFilter,
  requestSheetSelect,
  openScope,
})

const { handleSend, handleEditResend, acceptCard, declineCard } = useChatTurn({
  webChat,
  documents,
  chatInput,
  route,
  router,
  silentDispatch,
  currentViewState,
  closeScopePanel,
  scrollToBottom: () => chatMessagesRef.value?.scrollToBottomNow(),
})

const { uploadError, uploadFiles } = useChatUploads({
  attachDocument: webChat.attachDocument,
  detachDocument: webChat.detachDocument,
  documents,
  upload: indexing.upload,
  openDocsDrawer,
  t,
})

// ── Drag-and-drop: documents from the browser, and OS files ──
// The chat keeps its own drop target because the open thread IS the target (#83); the highlight is
// this component's own flag — which a drag crossing the panel's own children must not clear (MAR-1473).
const { active: dragOverChat, onDragLeave: onChatDragLeave } = useDragHighlight()
// Slow takes no attachments (MAR-1390), so on Slow the panel is not a drop target at all: a file
// dropped here would be uploaded and attached for a turn that cannot read it.
const { modelId } = useModelSelector()

function onChatDragOver(e: DragEvent) {
  if (modelId.value === 'slow' || !acceptsChatDrop(e.dataTransfer)) return
  e.preventDefault()
  e.dataTransfer!.dropEffect = 'copy'
  dragOverChat.value = true
}

function onChatDrop(e: DragEvent) {
  e.preventDefault()
  dragOverChat.value = false
  if (modelId.value === 'slow') return
  const nativeFiles = e.dataTransfer?.files
  if (nativeFiles && nativeFiles.length > 0) {
    e.stopPropagation()
    void uploadFiles(Array.from(nativeFiles))
    return
  }
  for (const item of documentsFromChatDrop(e.dataTransfer)) webChat.attachDocument(item)
}

// ── Conversation navigation (explicit, no watchers) ──
function clearConversation() {
  webChat.startNewChat()
  if (import.meta.client && route.path.startsWith('/app/c/')) router.replace('/app')
}

onMounted(() => {
  webChat.setScrollRequester(() => chatMessagesRef.value?.requestScroll())
  const prev = router.options.history.state.back as string | undefined
  cameFromHistory.value = !!prev && prev.startsWith('/app/history')
  // Reopens the split preview a reload would otherwise drop (`?preview=<id>`). Only the id is in the
  // URL, so the name is resolved the way `/app/docs/[id]` resolves its own title.
  const previewId = route.query.preview
  if (typeof previewId === 'string' && previewId) {
    documents.loadDetail(previewId)
      .then(detail => webChat.openDocPreview(previewId, detail.file_name || detail.name))
      .catch(err => console.warn('[ChatPanel] could not restore the preview from the URL:', err))
  }
})

onBeforeUnmount(() => webChat.setScrollRequester(null))

// Initial load, client-only (auth cookies aren't reliably available during SSR). `route.params.id` is
// a conversation id only on /app/c/[id]: this panel is docked globally, and on /app/cases/[id] that
// param is a CASE — loading it as a conversation poisons `conversationId` and 404s on save.
const initChatId = (route.path.startsWith('/app/c/') ? (route.params.id as string | undefined) : undefined)
  || (route.query.chat as string | undefined)

useAsyncData('chat-init', async () => {
  // A surface that opens this panel (the case hub, "New chat" inside a case) sets the case scope just
  // before the panel mounts, and the startNewChat() below would wipe it — so the next conversation
  // landed unlinked. Capture the pending scope of a not-yet-created chat BEFORE the reset.
  const pendingScope = !webChat.conversationId && webChat.activeCaseId
    ? { id: webChat.activeCaseId, name: webChat.activeCaseName ?? '' }
    : null
  // A REMOUNT is not a new chat (MAR-955): closing the floating popup unmounts this panel and the FAB
  // remounts it, with the conversation still in the store — possibly before `?chat=` names it.
  if (initChatId) await webChat.loadConversation(initChatId)
  else if (!webChat.conversationId) webChat.startNewChat()
  const promptParam = route.query.prompt as string | undefined
  if (promptParam) {
    webChat.startNewChat()
    chatInput.value = promptParam
    router.replace({ query: { ...route.query, prompt: undefined } })
  }
  // Belt and braces now that a load OFFERS its row's scope rather than adopting it.
  if (pendingScope && !webChat.conversationId) void webChat.setActiveCase(pendingScope)
}, { server: false })

// One composer, defined once and placed under the thread or on the welcome screen.
const [DefineComposer, ReuseComposer] = createReusableTemplate<{ placement: 'thread' | 'welcome' }>()
</script>

<template>
  <div
    class="flex h-full bg-background relative"
    :class="(lawRequest || docPreviewRequest) && !props.compact ? 'flex-col md:flex-row' : 'flex-col'"
    @dragover="onChatDragOver"
    @dragleave="onChatDragLeave"
    @drop="onChatDrop"
  >
    <!-- Renders nothing here; `ReuseComposer` below places it. Inside the root, so the panel keeps
         one root element and its fallthrough attributes. -->
    <DefineComposer v-slot="{ placement }">
      <ChatComposer
        v-model="chatInput"
        :placement="placement"
        :compact="props.compact"
        :upload-error="uploadError"
        @send="handleSend"
        @pick-document="documentPickerOpen = true"
        @upload-files="uploadFiles"
      />
    </DefineComposer>

    <FileDropOverlay :active="dragOverChat" :label="t('webChat.addToContext')">
      <template #icon><FileText class="h-8 w-8 text-primary" /></template>
    </FileDropOverlay>

    <div class="flex flex-col flex-1 min-w-0 min-h-0" :inert="previewCoversThread">
      <template v-if="webChat.hasActiveChat">
        <!-- Two presentations, one instance (MAR-127): teleported into the app bar when chat IS the
             sheet's main surface, so one header band spans the sheet and the context rail sits below
             it (Sistema Portafolio §07); its own band when it cannot borrow the bar (`ownHeaderBand`). -->
        <Teleport :to="`#${APP_BAR_SLOT_ID}`" :disabled="ownHeaderBand">
          <ChatHeader
            :compact="props.compact"
            :own-header-band="ownHeaderBand"
            :show-rail-toggle="showRailToggle"
            :came-from-history="cameFromHistory"
            @new-chat="clearConversation"
          />
        </Teleport>

        <div v-if="webChat.loadingConversation" class="flex-1 flex items-center justify-center">
          <Loader2 class="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
        <ChatMessages
          v-else
          ref="chatMessagesRef"
          :messages="webChat.messages"
          :is-streaming="webChat.isStreaming"
          :tool-activities="webChat.toolActivities"
          :awaiting-answer="webChat.awaitingAnswer"
          :citation-snippets="webChat.citationSnippets"
          :citations-resolving="webChat.citationsResolving"
          @resend="handleEditResend"
          @card-accept="acceptCard()"
          @card-decline="declineCard()"
          @card-open-form="webChat.openWorkflowForm()"
          @download-document="webChat.downloadDocument($event)"
          @fix-validation="webChat.recoverGrounding"
          @request-email="webChat.requestQueuedTurnEmail"
        />

        <ReuseComposer placement="thread" />
      </template>

      <ChatWelcome v-else :compact="props.compact">
        <ReuseComposer placement="welcome" />
      </ChatWelcome>
    </div>

    <!-- Desktop-only: drag to resize the split (mobile shows a full-screen overlay, nothing to
         resize). `ResizeHandle` reads its DOM siblings, so it must sit between the two columns.
         `max-size` caps the DOCUMENT panel: past ~900px the page stops growing and only adds margin. -->
    <ResizeHandle
      v-if="(docPreviewRequest || lawRequest) && !props.compact"
      class="hidden md:block"
      direction="horizontal"
      flush
      :min-size="320"
      :max-size="docPreviewRequest ? 900 : undefined"
    />

    <!-- The document preview and the law article share one slot (opening one clears the other).
         Phones get a full-screen overlay: a fixed-width `shrink-0` panel was wider than the viewport.
         So does the floating window, at every viewport width (`previewCoversThread`). -->
    <div
      v-if="docPreviewRequest"
      class="absolute inset-0 z-30 bg-background"
      :class="props.compact ? '' : 'md:static md:z-auto md:w-[480px] md:shrink-0'"
    >
      <ChatDocPreviewPanel />
    </div>
    <div
      v-else-if="lawRequest"
      class="absolute inset-0 z-30 bg-background"
      :class="props.compact ? '' : 'md:static md:z-auto md:w-[400px] md:shrink-0'"
    >
      <LawArticleView :norm-id="lawRequest.normId" :section-id="lawRequest.sectionId" />
    </div>

    <!-- Mounted only while open, so it loads on mount and needs no watcher. -->
    <ChatDocumentPicker
      v-if="documentPickerOpen"
      :attached-ids="webChat.contextDocumentIds"
      @select="docs => docs.forEach(doc => webChat.attachDocument(doc))"
      @close="documentPickerOpen = false"
    />
  </div>
</template>
